import React from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import WeaverLogo from "./WeaverLogo";

const HERO_VIMEO_ID = "1205890737";
const HERO_VIMEO_HASH = "4161490dcb";

const Hero = () => {
  const scrollToUseCases = () => {
    const el = document.querySelector("#use-cases");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center"
    >
      {/* Background video (Vimeo, full-bleed cover) */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <iframe
          src={`https://player.vimeo.com/video/${HERO_VIMEO_ID}?h=${HERO_VIMEO_HASH}&background=1&autoplay=1&loop=1&muted=1`}
          title="Weaver Media background"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            width: "100vw",
            height: "56.25vw",
            minHeight: "100vh",
            minWidth: "177.78vh",
          }}
          data-testid="hero-video"
        />
      </div>
      {/* Dark overlay for cinematic contrast */}
      <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
      {/* Bottom gradient fade into next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-[#050505]"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-12 pt-28 sm:pt-32 pb-36 sm:pb-44 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <WeaverLogo
            size="xl"
            className="mb-10 sm:mb-14 justify-center"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black uppercase tracking-tight leading-[0.95] text-white"
          data-testid="hero-headline"
        >
          Remote
          <br />
          Production
          <br />
          <span className="text-zinc-400">Agency</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 mx-auto max-w-2xl text-sm sm:text-base text-zinc-300 font-light tracking-wide leading-relaxed"
        >
          Your in-house content production team — without the overhead.
          <br />
          Senior-level video, animation, and audio on a per project basis or
          one flat monthly rate, all delivered remotely.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-col items-center gap-4 sm:gap-5"
        >
          <button
            onClick={scrollToUseCases}
            data-testid="hero-cta-explore"
            className="group inline-flex items-center gap-3 px-9 py-4 bg-white text-black font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-zinc-200 transition-all duration-300 hover:gap-5"
          >
            Explore Use Cases
            <ArrowDown
              size={16}
              className="transition-transform duration-500 group-hover:translate-y-0.5"
            />
          </button>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Link
              to="/subscription"
              data-testid="hero-cta-subscription"
              className="group inline-flex items-center gap-3 px-9 py-4 bg-transparent border border-white/25 text-white font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-300 hover:gap-5"
            >
              Explore Subscription
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:rotate-45"
              />
            </Link>
            <Link
              to="/pricing"
              data-testid="hero-cta-pricing"
              className="group inline-flex items-center gap-3 px-9 py-4 bg-transparent border border-white/25 text-white font-bold text-xs sm:text-sm uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-300 hover:gap-5"
            >
              Per Project Pricing
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:rotate-45"
              />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-3 text-zinc-400">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-zinc-400 to-transparent scroll-pulse" />
      </div>
    </section>
  );
};

export default Hero;
